import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Trash2, Pencil, Plus, Loader2, FileText } from "lucide-react";

// Organization-wide shared template library. Admins (Org Admin + Primary Admin/Owner) publish,
// edit and delete; members see them read-only in the report/compile Custom-template panel.
export default function OrgTemplates() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, name, structure_text}
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/custom-templates"); setItems((res.data || []).filter((x) => x.scope === "org")); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".docx") && !lower.endsWith(".pdf")) { toast.error(t("customTpl.badType")); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/custom-templates/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing({ name: file.name.replace(/\.(docx|pdf)$/i, ""), structure_text: res.data.structure_text || "", source_type: "upload" });
    } catch (err) { toast.error(err?.response?.data?.detail || t("customTpl.extractFailed")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!editing.name.trim()) { toast.error(t("customTpl.nameRequired")); return; }
    if (!(editing.structure_text || "").trim()) { toast.error(t("orgTpl.structureRequired")); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/custom-templates/${editing.id}`, { name: editing.name.trim(), structure_text: editing.structure_text });
        toast.success(t("customTpl.updated"));
      } else {
        await api.post("/custom-templates", { name: editing.name.trim(), structure_text: editing.structure_text, source_type: editing.source_type || "describe", scope: "org" });
        toast.success(t("orgTpl.published"));
      }
      setEditing(null); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("customTpl.updateFailed")); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t("customTpl.confirmDelete"))) return;
    try { await api.delete(`/custom-templates/${id}`); setItems((a) => a.filter((x) => x.id !== id)); toast.success(t("customTpl.deleted")); }
    catch (err) { toast.error(t("customTpl.deleteFailed")); }
  };

  return (
    <div data-testid="org-templates-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-semibold text-navy">{t("orgTpl.title")}</h3>
          <p className="text-sm text-slatebody">{t("orgTpl.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".docx,.pdf" onChange={onUpload} className="hidden" data-testid="org-tpl-upload-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="org-tpl-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("customTpl.uploadBtn")}
          </Button>
          <Button onClick={() => setEditing({ name: "", structure_text: "", source_type: "describe" })} data-testid="org-tpl-new-btn">
            <Plus className="w-4 h-4" /> {t("orgTpl.new")}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slatebody">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#D9D9E3] p-8 text-center" data-testid="org-tpl-empty">
          <FileText className="w-6 h-6 mx-auto text-slatebody mb-2" />
          <p className="text-sm text-slatebody">{t("orgTpl.empty")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((tpl) => (
            <div key={tpl.id} className="flex items-start justify-between gap-3 rounded-lg border border-[#E4E4EC] p-3" data-testid={`org-tpl-${tpl.id}`}>
              <div className="min-w-0">
                <p className="font-medium text-navy text-sm">{tpl.name}</p>
                <p className="font-mono text-[11px] text-slatebody mt-0.5 truncate">{(tpl.structure_text || "").split("\n").filter(Boolean).slice(0, 3).join(" · ")}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => setEditing({ id: tpl.id, name: tpl.name, structure_text: tpl.structure_text || "" })}
                  className="text-slatebody hover:text-navy" data-testid={`org-tpl-edit-${tpl.id}`} aria-label={t("customTpl.edit")}><Pencil className="w-4 h-4" /></button>
                <button type="button" onClick={() => del(tpl.id)} className="text-slatebody hover:text-red-600" data-testid={`org-tpl-delete-${tpl.id}`} aria-label={t("customTpl.delete")}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="org-tpl-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? t("customTpl.editTitle") : t("orgTpl.newTitle")}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t("customTpl.namePlaceholder")}</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="h-9 text-sm mt-1" data-testid="org-tpl-name" />
              </div>
              <div>
                <Label className="text-xs">{t("customTpl.describeLabel")}</Label>
                <textarea value={editing.structure_text} onChange={(e) => setEditing({ ...editing, structure_text: e.target.value })} rows={7}
                  placeholder={t("customTpl.describePlaceholder")} className="mt-1 w-full text-sm rounded-md border border-[#C5C5D0] p-2 focus:outline-none focus:ring-1 focus:ring-navy" data-testid="org-tpl-structure" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} data-testid="org-tpl-save">{saving ? t("customTpl.saving") : t("orgTpl.publish")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
